import { useEffect, useState } from 'react'
import './web-test-motion.css'
import './web-test-hero.css'
import { WebsiteThemeToggle } from './WebsiteThemeToggle'
import { WebTestHowItWorks } from './WebTestHowItWorks'
import { WebTestMarkets } from './WebTestMarkets'
import { WebTestPayoutExamples } from './WebTestPayoutExamples'
import { WebTestWhy } from './WebTestWhy'
import { WebTestInfrastructure } from './WebTestInfrastructure'
import { WebTestFunding } from './WebTestFunding'
import { WebTestFaq, HOMEPAGE_FAQ_ITEMS } from './WebTestFaq'
import { WebTestFinalCta } from './WebTestFinalCta'
import { WebTestFooter } from './WebTestFooter'
import {
  DEFAULT_INSTRUMENT_ID,
  getAvailableInstruments,
  getInstrumentById,
} from './instruments'
import './web-test-layout.css'
import { HERO_RISK_NOTICE, PREVIEW_FIGURES_NOTE } from './risk-copy'
import { SeoHead } from './seo/SeoHead'
import { defaultPageGraph, faqPageJsonLd } from './seo/jsonld'
import { getPageSeo } from './seo/pages'
import { trackClientLogin, trackMarketSelected, trackOpenAccount, trackStrategySelected } from '../analytics/track'
import { portalLinks } from './portal-links'
import { BrandLogo } from '../shared/BrandLogo'

const NAV = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Strategies', href: '#strategies' },
  { label: 'Markets', href: '#markets' },
  { label: 'Platform', href: '#infrastructure' },
  { label: 'Funding', href: '#funding' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Company', href: '/about' },
  { label: 'Support', href: '/support' },
] as const

type StrategyId = 'safe' | 'balance' | 'aggressive'
type Direction = 'buy' | 'sell'

/** Illustrative preview values — aligned with Strategies section examples */
const STRATEGIES: Record<
  StrategyId,
  {
    label: string
    returnLabel: string
    intensity: number
    buyTarget: string
    buyStop: string
    buyPotential: string
    sellTarget: string
    sellStop: string
    sellPotential: string
  }
> = {
  safe: {
    label: 'SAFE',
    returnLabel: '160%',
    intensity: 2,
    buyTarget: '2.0%',
    buyStop: '1.0%',
    buyPotential: '+60%',
    sellTarget: '2.0%',
    sellStop: '1.0%',
    sellPotential: '+60%',
  },
  balance: {
    label: 'BALANCE',
    returnLabel: '250%',
    intensity: 3,
    buyTarget: '3.0%',
    buyStop: '1.0%',
    buyPotential: '+150%',
    sellTarget: '3.0%',
    sellStop: '1.0%',
    sellPotential: '+150%',
  },
  aggressive: {
    label: 'AGGRESSIVE',
    returnLabel: '400%',
    intensity: 5,
    buyTarget: '4.0%',
    buyStop: '1.0%',
    buyPotential: '+300%',
    sellTarget: '4.0%',
    sellStop: '1.0%',
    sellPotential: '+300%',
  },
}

function IntensityBars({ level }: { level: number }) {
  return (
    <div className="wt-intensity" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= level ? 'is-on' : undefined} />
      ))}
    </div>
  )
}

export function WebTestHeroPage() {
  const [ready, setReady] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [instrumentId, setInstrumentId] = useState(DEFAULT_INSTRUMENT_ID)
  const [strategy, setStrategy] = useState<StrategyId>('balance')
  const [direction, setDirection] = useState<Direction>('buy')

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true))
    return () => window.cancelAnimationFrame(id)
  }, [])

  const instruments = getAvailableInstruments()
  const instrument = getInstrumentById(instrumentId) ?? instruments[0]
  const s = STRATEGIES[strategy]
  const target = direction === 'buy' ? s.buyTarget : s.sellTarget
  const stop = direction === 'buy' ? s.buyStop : s.sellStop
  const potential = direction === 'buy' ? s.buyPotential : s.sellPotential
  const homeSeo = getPageSeo('/')!
  const homeJsonLd = defaultPageGraph(homeSeo, [faqPageJsonLd(HOMEPAGE_FAQ_ITEMS)])

  return (
    <div className={`wt-page${ready ? ' is-ready' : ''}`}>
      <SeoHead path="/" jsonLd={homeJsonLd} />
      <header className="wt-header">
        <div className="wt-header__inner">
          <BrandLogo className="wt-logo" tone="light" href="/" />

          <nav className="wt-nav" aria-label="Primary">
            {NAV.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <div className="wt-header__end">
            <div className="wt-header__actions">
              <a
                className="wt-btn wt-btn--ghost"
                href={portalLinks.login}
                onClick={() => trackClientLogin('header')}
              >
                Sign In
              </a>
              <a
                className="wt-btn wt-btn--primary"
                href={portalLinks.register}
                onClick={() => trackOpenAccount('header')}
              >
                Open Live Account
              </a>
            </div>
            <WebsiteThemeToggle />
            <button
              type="button"
              className="wt-menu-toggle"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="wt-menu-toggle__bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>

        <div className="wt-mobile-menu" hidden={!menuOpen}>
          <nav aria-label="Mobile">
            {NAV.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="wt-mobile-menu__actions">
            <a
              className="wt-btn wt-btn--ghost"
              href={portalLinks.login}
              onClick={() => {
                setMenuOpen(false)
                trackClientLogin('header')
              }}
            >
              Sign In
            </a>
            <a
              className="wt-btn wt-btn--primary"
              href={portalLinks.register}
              onClick={() => {
                setMenuOpen(false)
                trackOpenAccount('header')
              }}
            >
              Open Live Account
            </a>
          </div>
        </div>
      </header>

      <main className="wt-main">
      <section className="wt-hero" aria-label="Hero">
        <div className="wt-hero__grid">
          <div className="wt-hero__copy">
            <p className="wt-eyebrow wt-anim-eyebrow">Structured Prediction Trading</p>
            <h1 className="wt-heading wt-anim-heading">
              Prediction Trading
              <br />
              with <span className="wt-heading__accent">Defined Targets.</span>
            </h1>
            <p className="wt-body wt-anim-body">
              Choose a prediction strategy. Know your target, stop and potential return before you
              trade. You only choose BUY or SELL.
            </p>
            <div className="wt-anim-cta">
              <div className="wt-cta-row">
                <a
                  className="wt-btn wt-btn--primary wt-btn--hero"
                  href={portalLinks.register}
                  onClick={() => trackOpenAccount('hero')}
                >
                  Open Live Account
                </a>
                <a className="wt-btn wt-btn--secondary wt-btn--hero" href="#how-it-works">
                  See How It Works
                </a>
              </div>
              <p className="wt-hero-risk">{HERO_RISK_NOTICE}</p>
            </div>
          </div>

          <div className="wt-visual wt-anim-visual">
            <div className="wt-product" aria-label="Start a Prediction">
              <div className="wt-product__head">
                <p className="wt-product__title">Start a Prediction</p>
                <p className="wt-product__hint">Interactive preview</p>
              </div>

              <section className="wt-step">
                <p className="wt-step__label">
                  <span>Instrument</span> {instrument?.name} · {instrument?.symbol}
                </p>
                <div className="wt-instruments" role="tablist" aria-label="Instrument">
                  {instruments.map((item) => {
                    const selected = instrumentId === item.id
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        className={`wt-instrument${selected ? ' is-selected' : ''}`}
                        onClick={() => {
                          setInstrumentId(item.id)
                          if (item.id === 'gold' || item.id === 'silver') {
                            trackMarketSelected(item.id, 'hero')
                          }
                        }}
                      >
                        <span className="wt-instrument__name">{item.name}</span>
                        <span className="wt-instrument__symbol">{item.symbol}</span>
                      </button>
                    )
                  })}
                </div>
              </section>

              <section className="wt-step">
                <p className="wt-step__label">
                  <span>Step 1</span> Choose Strategy
                </p>
                <div className="wt-strategies" role="tablist" aria-label="Strategy">
                  {(Object.keys(STRATEGIES) as StrategyId[]).map((id) => {
                    const item = STRATEGIES[id]
                    const selected = strategy === id
                    return (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        className={`wt-strategy${selected ? ' is-selected' : ''} wt-strategy--${id}`}
                        onClick={() => {
                          setStrategy(id)
                          trackStrategySelected(id, 'hero')
                        }}
                      >
                        <span className="wt-strategy__name">{item.label}</span>
                        <IntensityBars level={item.intensity} />
                        <span className="wt-strategy__return">{item.returnLabel}</span>
                      </button>
                    )
                  })}
                </div>
              </section>

              <section className="wt-step">
                <p className="wt-step__label">
                  <span>Step 2</span> Choose Direction
                </p>
                <div className="wt-direction">
                  <button
                    type="button"
                    className={`wt-dir wt-dir--buy${direction === 'buy' ? ' is-selected' : ''}`}
                    onClick={() => setDirection('buy')}
                    aria-pressed={direction === 'buy'}
                  >
                    BUY
                  </button>
                  <button
                    type="button"
                    className={`wt-dir wt-dir--sell${direction === 'sell' ? ' is-selected' : ''}`}
                    onClick={() => setDirection('sell')}
                    aria-pressed={direction === 'sell'}
                  >
                    SELL
                  </button>
                </div>
              </section>

              <section className="wt-step wt-step--preview">
                <p className="wt-step__label">
                  <span>Step 3</span> Preview Outcome
                </p>
                <div className="wt-preview" key={`${instrumentId}-${strategy}-${direction}`}>
                  <div className="wt-metric">
                    <span>Target</span>
                    <strong className="wt-metric__value">{target}</strong>
                  </div>
                  <div className="wt-metric">
                    <span>Stop</span>
                    <strong className="wt-metric__value">{stop}</strong>
                  </div>
                  <div className="wt-metric">
                    <span>Potential Return</span>
                    <strong className="wt-metric__value wt-metric__value--accent">{potential}</strong>
                  </div>
                </div>
                <p className="wt-preview__note">
                  Illustrative example on {instrument?.name} ({instrument?.symbol}).{' '}
                  {PREVIEW_FIGURES_NOTE}
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
      <WebTestHowItWorks />
      <WebTestPayoutExamples />
      <WebTestMarkets />
      <WebTestWhy />
      <WebTestInfrastructure />
      <WebTestFunding />
      <WebTestFaq />
      <WebTestFinalCta />
      </main>
      <WebTestFooter />
    </div>
  )
}
